import { Controller } from "@hotwired/stimulus"

// The details somebody prepares before registration opens (docs/07 "Prepare
// your details", T50c).
//
// Two hundred companies race for booths the moment this opens, and the ones
// who lose are the ones still typing an address. So the form can be filled in
// beforehand and comes back prefilled at Start, which turns steps 3 and 4 into
// a read-through.
//
// **The draft lives in the BROWSER, never on the server.** docs/07 is explicit
// about this and it is not an implementation convenience: a half-filled form
// held server-side is personal data nobody consented to leave with us, it
// would need a retention rule and a purge job of its own, and it would make
// "clear saved details" a promise about a database rather than a fact about
// this machine. The logo is the exception — it is a file, it cannot live in
// localStorage, so its blob goes up tagged `prepared` and the draft keeps only
// the signed id.
//
// Storage can throw (private windows, blocked site data) and can come back
// empty at any time. Every access is guarded and the page works without it:
// the draft is a convenience, never a step of the flow.
export default class extends Controller {
  static targets = ["saved", "cleared"]

  // Anything the registration form would submit, minus the controls a draft
  // cannot sensibly carry: a file input's value is not settable, and a radio
  // or checkbox's `value` is its option, not its state. Found by NAME rather
  // than by marking every field, so a new field on the form is remembered
  // without a second list to keep in step.
  static SKIP_TYPES = [ "file", "radio", "checkbox", "submit", "button" ]
  static values = {
    key: { type: String, default: "vara:prepared-details" },
    // Set on the page that follows a successful submit: the details are on the
    // registration now, and a draft left behind would prefill the NEXT one
    // with somebody's finished application.
    clear: { type: Boolean, default: false }
  }

  connect() {
    if (this.clearValue) return this.clear({ quiet: true })

    this.restore()
  }

  get fields() {
    return Array.from(this.element.querySelectorAll('[name^="exhibitor_registration["]'))
      .filter((field) => !this.constructor.SKIP_TYPES.includes(field.type))
  }

  // Called on every input. Cheap enough to do synchronously: a dozen short
  // strings, and a debounce would lose the last keystrokes of somebody who
  // closes the tab mid-sentence.
  save() {
    const draft = {}
    this.fields.forEach((field) => {
      if (field.value) draft[this.nameOf(field)] = field.value
    })

    this.write(draft)
    this.announce(this.savedTargets, Object.keys(draft).length > 0)
  }

  restore() {
    const draft = this.read()
    if (!draft) return

    this.fields.forEach((field) => {
      // A value the server has just refused (a logo whose upload expired):
      // putting it back would fail the next submit the same way.
      if (field.dataset.draftForget) return delete draft[this.nameOf(field)]

      const value = draft[this.nameOf(field)]
      // Never overwrite what is already there: a re-render after a validation
      // error carries the person's newest answers, and the draft is older.
      if (value && !field.value) field.value = value
    })
    this.write(draft)

    this.announce(this.savedTargets, true)
  }

  clear({ quiet = false } = {}) {
    try {
      window.localStorage.removeItem(this.keyValue)
    } catch {
      // Nothing to clear, because nothing could be stored.
    }

    if (quiet) return

    this.fields.forEach((field) => { field.value = "" })
    this.announce(this.savedTargets, false)
    this.announce(this.clearedTargets, true)
  }

  // The prepare form is a form so its fields behave like fields; submitting it
  // would mean nothing, because there is nothing to submit to yet.
  stop(event) {
    event.preventDefault()
  }

  // --- Storage, which is allowed to fail ------------------------------------

  read() {
    try {
      return JSON.parse(window.localStorage.getItem(this.keyValue) || "null")
    } catch {
      return null
    }
  }

  write(draft) {
    try {
      window.localStorage.setItem(this.keyValue, JSON.stringify(draft))
    } catch {
      // A private window, or site data turned off. The form still works; it
      // simply will not remember.
    }
  }

  // The form field's own name, so the draft keys match the form on both pages
  // without a second list to keep in step.
  nameOf(field) { return field.name }

  announce(targets, shown) {
    targets.forEach((target) => { target.hidden = !shown })
  }
}
